import React from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const InstructionCardContainer = styled(Card)(({ theme, instructiontype }) => ({
  maxWidth: '700px',
  margin: '40px auto',
  padding: '40px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#FFFFFF',
  animation: 'fadeIn 0.4s ease-in-out',
  border: instructiontype === 'warning' 
    ? '2px solid #FF9800' 
    : instructiontype === 'success' 
    ? '2px solid #4CAF50'
    : '2px solid #996CFE',
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px',
    margin: '20px 16px',
  },
}));

const IconContainer = styled(Box)(({ instructiontype }) => ({
  width: '80px',
  height: '80px',
  margin: '0 auto 24px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: instructiontype === 'warning'
    ? 'rgba(255, 152, 0, 0.1)'
    : instructiontype === 'success'
    ? 'rgba(76, 175, 80, 0.1)'
    : 'rgba(153, 108, 254, 0.1)',
}));

const InstructionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#2D3748',
  textAlign: 'center',
  marginBottom: '20px',
  lineHeight: 1.4,
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
    marginBottom: '16px',
  },
}));

const InstructionContent = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  color: '#4A5568',
  textAlign: 'center',
  lineHeight: 1.8,
  marginBottom: '32px',
  whiteSpace: 'pre-line',
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
    marginBottom: '24px',
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  marginTop: '32px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    gap: '12px',
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  padding: '12px 32px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  minWidth: '140px',
  transition: 'all 0.2s ease',
  ...(variant === 'contained' && {
    backgroundColor: '#996CFE',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#7C4DFF',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(153, 108, 254, 0.3)',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: '#E0E0E0',
    color: '#4A5568',
    '&:hover': {
      borderColor: '#996CFE',
      backgroundColor: 'rgba(153, 108, 254, 0.05)',
    },
  }),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 'unset',
  },
}));

const InstructionCard = ({
  title,
  content,
  type = 'info',
  showPrevious = false,
  showNext = true,
  onPrevious,
  onNext,
  nextButtonText = 'Next',
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <WarningAmberIcon sx={{ fontSize: '40px', color: '#FF9800' }} />;
      case 'success':
        return <CheckCircleOutlineIcon sx={{ fontSize: '40px', color: '#4CAF50' }} />;
      case 'info':
      default:
        return <InfoOutlinedIcon sx={{ fontSize: '40px', color: '#996CFE' }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#F5F6FA',
        padding: '20px',
      }}
    >
      <InstructionCardContainer instructiontype={type}>
        <CardContent sx={{ padding: 0 }}>
          <IconContainer instructiontype={type}>
            {getIcon()}
          </IconContainer>

          <InstructionTitle>{title}</InstructionTitle>

          <InstructionContent>{content}</InstructionContent>

          <ButtonContainer>
            {showPrevious ? (
              <StyledButton
                variant="outlined"
                onClick={onPrevious}
                disabled={!onPrevious}
              >
                Previous
              </StyledButton>
            ) : (
              <Box sx={{ width: '140px', display: { xs: 'none', sm: 'block' } }} />
            )}

            {showNext && (
              <StyledButton
                variant="contained"
                onClick={onNext}
                disabled={!onNext}
              >
                {nextButtonText}
              </StyledButton>
            )}
          </ButtonContainer>
        </CardContent>
      </InstructionCardContainer>
    </Box>
  );
};

export default InstructionCard;
